import {useCurrentHost} from '#imports';

export type QueryParameters = {
    [name: string]: string | string[] | undefined;
};

export type ExternalRoute = {
    path: string;
    query?: QueryParameters;
};

export function useBaseUrl() {
    const host = useCurrentHost().getCurrentHost().replace(/\/+$/, '');
    const baseUrl = `https://${host}`;

    /**
     * Like `useRouter().resolve(...)` but for external links to avoid nuxt complaining about unresolvable routes.
     */
    function resolve(to: ExternalRoute) {
        const url = new URL(to.path, baseUrl);
        if (to.query) {
            for (const [name, values] of Object.entries(to.query)) {
                if (typeof values === 'string') {
                    url.searchParams.append(name, values);
                } else if (values) {
                    for (const value of values) {
                        url.searchParams.append(name, value);
                    }
                }
            }
        }
        return `${url.pathname}${url.search}`;
    }

    /**
     * Resolves the given absolute path against the server URL.
     */
    function parseQueryParams(url: string): QueryParameters {
        const absoluteUrl = new URL(url, baseUrl);
        const queryParams: QueryParameters = {};
        for (const [name, value] of absoluteUrl.searchParams.entries()) {
            if (name in queryParams) {
                const currentValue = queryParams[name];
                if (typeof currentValue === 'string') {
                    queryParams[name] = [currentValue, value];
                } else if (currentValue) {
                    currentValue.push(value);
                }
            } else {
                queryParams[name] = value;
            }
        }
        return queryParams;
    }

    return {
        resolve,
        parseQueryParams,
    };
}
